/**
 * CustomerProjectWallet.
 * 
 * This component is used in the reseller's customer page, and shows wallet balance, limit, settings, and
 * funding history. This is read-only; to change budget settings, the reseller needs to make changes
 * in an SO.
 * 
 * This is not backward-compatible with older models.  Instead, while legacy scenarios are supported, they
 * will be handled with different components.
 * 
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 
import PropTypes from 'prop-types';
import { Button, Grid, Paper, 
         Tooltip, Typography } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import SettingsBackupRestore from '@mui/icons-material/SettingsBackupRestore';
import ServerError from '../ui/ServerError';
import { NumericFormat } from 'react-number-format';
import LoadingSpinner from '../ui/LoadingSpinner';
import TopUps from '../account/TopUps';
import CustomerWalletBalanceTiles from '../home/CustomerWalletBalanceTiles';
import SpendingWidgets from '../home/SpendingWidgets';
import { getValueForRender } from '../../utils/InputUtils';

import AppBindStyles from '../../AppBindStyles';
import { green } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();

/* Need funding sources for the individual topup */
const CustomerProjectWalletQuery = graphql`
query CustomerProjectWalletQuery($accountId: UUID!) {
  accountTByAccountId(accountId: $accountId) {
    accountTByParentId {
     accountName
     logo
    }
    accountName
    contactFirstName
    contactLastName
    contactEmail
    accountType
    useBudgets
    autoTopupActive
    payFromBalance
    initialBalanceAmount
    autoTopupAmount
    autoTopupThreshhold
    rAutoTopupAmount
    rAutoTopupThreshold
    maxCharge
    budgetApprovedCount
    floatLimit
    requestedMaxCharge
    requestedFloatLimit
    rMaxCharge
    maxMonthlyBudget
    rMaxMonthlyBudget
    projectTsByCustomerAccountId {
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        walletTByCustomerWalletId {
          walletId
          initialBalanceAmount
          autoTopupAmount
          rAutoTopupAmount
          rAutoTopupThreshold
          autoTopupThreshold
          rMaxMonthlyBudget
          maxMonthlyBudget
          rMaxCharge
          maxCharge
          floatLimit
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  sectionBlock: {
    marginBottom: 12,  
  },
  paper: {
    textAlign: 'left',
    //color: theme.palette.text.secondary,
    maxWidth: 1122,
    paddingTop: 16,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
  },
  currencyField: {
    width: 144,
  },
  currencyField2: {
    width: 176,
  },
  currencyField3: {
    width: 100,
    marginTop: 4,
    marginBottom: 4,
    marginRight: 8,
  },
  currencyLabelField: {
    width: 130,
    marginTop: 10,
    marginBottom: 4,
    // marginRight: 8,
  },
  noBudgetHolder: {
    width: 108,
    marginTop: 12,
    marginBottom: 8,
    marginRight: 8,
    textAlign: 'right',
  },
  currencyFieldSlider: {
    marginLeft: 8,
    width:64,
  },
  success: {
    backgroundColor: green[300],
  },
  error: {
    backgroundColor: theme.palette.error.light,
  },

});

class CustomerProjectWallet extends Component {
  constructor(props)
  {
    super(props);
    this.state = {
      initialBalanceAmount:    null,
      rAutoTopupAmount:        null,
      rAutoTopupThreshold:     null,
      maxCharge:               null,
      floatLimit:              null,
      requestedMaxCharge:      9900,
      requestedFloatLimit:     null,
      maxMonthlyBudget:        null,
      rMaxMonthlyBudget:       null,
      rMaxMonthlyBudgetSet:    false, // Workaround to indicate if the state value is null or unset

      accountFetched: true,
      forceFetch: 0,

      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,

      confirmSave: false,
    };

  }

  /*
   * Invite a (customer) user to approve budgets and put a funding source on file to pay for the subscription.
   * This method will be passed to an invite component.
   */
  inviteCustomerUser = (bcUser) => {
    // If the user already exists, send them to the user edit to invite using that current user.
    // Otherwise, send to create.

    localStorage.setItem('budgetReturn', bcUser.accountId);

    if (bcUser.userid != null && bcUser.userid !== '') {
      let link = 'useredit?userId=' + bcUser.userid + '&accountId=' + bcUser.accountId;
      this.props.history.push(link);
    } else {
      let propsToCache = {'contactLastName'  : bcUser.lastname,
                          'contactFirstName' : bcUser.firstname,
                          'contactEmail'     : bcUser.email};
      localStorage.setItem('userCreateParamProps', JSON.stringify(propsToCache));
      let link = 'usercreate?accountId=' + bcUser.accountId;
      this.props.history.push(link);
    }
  }
  
  goToSO(props) {
    
    let propsToCache = {
      fromRequestApproval: true,
      accountId: this.props.accountId,
      companyName: props.accountTByAccountId.accountName,
      contactLastName: props.accountTByAccountId.contactLastName, 
      contactFirstName: props.accountTByAccountId.contactFirstName, 
      contactEmail:  props.accountTByAccountId.contactEmail};
    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));


    this.props.history.push(`/subscriptionorder`);
  }

  goToSOWithoutBudget(props) {
    
    let propsToCache = {
      accountId: this.props.accountId,
      companyName: props.accountTByAccountId.accountName,
      contactLastName: props.accountTByAccountId.contactLastName, 
      contactFirstName: props.accountTByAccountId.contactFirstName, 
      contactEmail:  props.accountTByAccountId.contactEmail};
    localStorage.setItem('soCustomerParms', JSON.stringify(propsToCache));

    this.props.history.push(`/subscriptionorder`);
  }

  render() {
    const classes = this.props.classes;

    const { logError } = this.props.auth;       
    
    let acctId = this.props.accountId;
    
    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={CustomerProjectWalletQuery}
        variables={{accountId: acctId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {

            // Get display values from state if set, props if not
            let theAccount = props.accountTByAccountId;
            let theProject = theAccount.projectTsByCustomerAccountId.nodes[0];
            let theWallet  = theProject.walletTByCustomerWalletId;

            // TODO: return if no wallet or project

            // let currentRequestedFloatLimitNum = getValueForRender(this.state.requestedFloatLimit, theAccount.requestedFloatLimit, 0);

            let showInitialValue = false;
            if (theProject) {
              if (theProject.budgetApprovedCount === 0) {
                showInitialValue = true;
              }
            } else if (props.accountTByAccountId.budgetApprovedCount === 0) {
              showInitialValue = true;
            }

            let initialBalanceAmount = getValueForRender(theWallet.initialBalanceAmount, 'Not set');

            // Auto-topup threshold
            let pendingAutoTopupThreshold = getValueForRender(theWallet.rAutoTopupThreshold, 'Not set');
            let autoTopupThreshold        = getValueForRender(theWallet.autoTopupThreshold, 'Not set');
            let autoTopupThresholdTooltip = null;
            if (pendingAutoTopupThreshold !== 'Not set' && autoTopupThreshold !== 'Not set' && pendingAutoTopupThreshold !== autoTopupThreshold) {
              autoTopupThresholdTooltip = 'Pending change from ' + Number(autoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            }            

            // Auto-topup amount
            let pendingAutoTopupAmount = getValueForRender(theWallet.rAutoTopupAmount, 'Not set');
            let autoTopupAmount        = getValueForRender(theWallet.autoTopupAmount, 'Not set');
            let autoTopupAmountTooltip = null;
            if (pendingAutoTopupAmount !== 'Not set' && autoTopupAmount !== 'Not set' && pendingAutoTopupAmount !== autoTopupAmount) {
              autoTopupAmountTooltip = 'Pending change from ' + Number(autoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            }

            // Monthly limit
            let pendingMaxMonthlyBudget = getValueForRender(theWallet.rMaxMonthlyBudget, 0);
            let maxMonthlyBudget        = getValueForRender(theWallet.maxMonthlyBudget, 0);
            let maxMonthlyTooltip       = null;

            let pendingMaxMonthlyBudgetString = 'No limit';
            if (pendingMaxMonthlyBudget != null && pendingMaxMonthlyBudget > 0) {
              pendingMaxMonthlyBudgetString = pendingMaxMonthlyBudget;
            }

            let maxMonthlyBudgetString = 'No limit';
            if (maxMonthlyBudget != null && maxMonthlyBudget > 0) {
              maxMonthlyBudgetString = maxMonthlyBudget;
            }

            if (pendingMaxMonthlyBudgetString !== maxMonthlyBudgetString) {
              if (pendingMaxMonthlyBudgetString === 'No limit') {
                maxMonthlyTooltip = 'Pending change to remove monthly spend limit (' + Number(maxMonthlyBudgetString).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
              } else {
                if (maxMonthlyBudgetString === 'No limit') {
                  maxMonthlyTooltip = 'Pending change from no limit';
                } else {
                  maxMonthlyTooltip = 'Pending change from ' + Number(maxMonthlyBudgetString).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
                }
              }
            }

            // Denominator for spending widget
            let monthlyDenominator = maxMonthlyBudget;
            if (monthlyDenominator === 0) {
              monthlyDenominator = null;
            }

            return (
              <Grid container spacing={2} direction="row" justifyContent="flex-start" className={classes.sectionBlock}>
                <Grid item xs={12}>
                  <Grid container spacing={0} direction="column" justifyContent="flex-start">
                    <Grid item>
                      <Typography variant="h6">Customer expense wallet</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body2">Your customer's expense wallet is used to pay for subscriptions that you provision and bill to them.</Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2} direction="row" justifyContent="flex-start">
                    <Grid item xs={12}>
                      <Paper className={classes.paper}>
                        <Grid container spacing={2} direction="row">
                          <Grid item xs={12}>
                            <Typography align="left" variant="h6">Balances</Typography>
                          </Grid>

                          {/* Balance widgets */}
                          <Grid item xs={12}>
                            <Grid container spacing={3} justifyContent="flex-start">
                              <Grid item>
                                <CustomerWalletBalanceTiles auth={this.props.auth} walletId={theWallet.walletId} floatLimit={theWallet.floatLimit}/>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <Grid container spacing={1} direction="row" justifyContent="flex-start">

                              {/* Only show initially fund on first time use - that is, until a budget has been accepted */}
                              {showInitialValue === true &&
                              <Grid item xs={12}>
                                <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                  <Grid item>
                                    <Typography variant="body1" style={AppBindStyles.boldLabel}>{"Initially fund balance to"}</Typography>
                                  </Grid>
                                  <Grid item > 
                                    {initialBalanceAmount === 'Not set' &&
                                      <Typography variant="body1">{initialBalanceAmount}</Typography>
                                    }
                                    {initialBalanceAmount !== 'Not set' &&
                                        <Typography variant="body1">
                                          <NumericFormat
                                            value={initialBalanceAmount}
                                            valueIsNumericString
                                            prefix={'$'} 
                                            displayType="text"
                                            decimalScale={2}
                                            fixedDecimalScale
                                            thousandSeparator/>
                                        </Typography>
                                    }
                                  </Grid>
                                </Grid>
                              </Grid>}

                              <Grid item xs={12}>
                                <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                  <Grid item>
                                    <Typography variant="body1" style={AppBindStyles.boldLabel}>When balance falls below</Typography>
                                  </Grid>
                                  <Grid item >
                                    <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                      {autoTopupThresholdTooltip != null &&
                                      <Grid item>
                                        <Tooltip placement='bottom' title={autoTopupThresholdTooltip}>
                                          <SettingsBackupRestore 
                                          fontSize="small" 
                                          style={{verticalAlign: "-8%"}}
                                          />
                                        </Tooltip>
                                      </Grid>}
                                      <Grid item>
                                        {pendingAutoTopupThreshold === 'Not set' &&
                                          <Typography variant="body1">{pendingAutoTopupThreshold}</Typography>}
                                        {pendingAutoTopupThreshold !== 'Not set' &&
                                          <Typography variant="body1">
                                            <NumericFormat
                                              value={pendingAutoTopupThreshold}
                                              valueIsNumericString
                                              prefix={'$'} 
                                              displayType="text"
                                              decimalScale={2}
                                              fixedDecimalScale
                                              thousandSeparator/>
                                          </Typography>}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12}>
                                <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                  <Grid item>
                                    <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>
                                  </Grid>
                                  <Grid item > 
                                    <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                      {autoTopupAmountTooltip != null &&
                                      <Grid item>
                                        <Tooltip placement='bottom' title={autoTopupAmountTooltip}>
                                          <SettingsBackupRestore 
                                          fontSize="small" 
                                          style={{verticalAlign: "-8%"}}
                                          />
                                        </Tooltip>
                                      </Grid>}
                                      <Grid item>
                                        {pendingAutoTopupAmount === 'Not set' &&
                                          <Typography variant="body1">{pendingAutoTopupAmount}</Typography>}
                                        {pendingAutoTopupAmount !== 'Not set' &&
                                          <Typography variant="body1">
                                            <NumericFormat
                                              value={pendingAutoTopupAmount}
                                              valueIsNumericString
                                              prefix={'$'} 
                                              displayType="text"
                                              decimalScale={2}
                                              fixedDecimalScale
                                              thousandSeparator/>
                                          </Typography>}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item style={AppBindStyles.bottomButton}>
                            {this.props.invitationNeeded !== true &&
                            <Tooltip placement="bottom" title="Send a revised subscription order to your customer to request a change in the funding settings for their wallet.">
                              <Button color="primary" variant="outlined" 
                                      onClick={(e) => {this.goToSO(props);}}>
                                Request wallet change
                              </Button>
                            </Tooltip>}
                            {this.props.invitationNeeded === true &&
                            <Tooltip placement="bottom" title="Send a subscription order to your customer to request funding for their wallet.">
                              <Button color="primary" variant="outlined"  
                                      onClick={(e) => {this.goToSOWithoutBudget(props);}}>
                                Invite and request wallet funding
                              </Button>
                            </Tooltip>}
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper className={classes.paper}>
                        <Grid container spacing={2} direction="row" >
                          <Grid item xs={12}>
                            <Typography align="left" variant="h6">Spending</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <SpendingWidgets accountId={acctId} spendLimit={monthlyDenominator}/>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Monthly spend limit</Typography>
                              </Grid>
                              <Grid item>
                                <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                  {maxMonthlyTooltip != null &&
                                  <Grid item>
                                    <Tooltip placement='bottom' title={maxMonthlyTooltip}>
                                      <SettingsBackupRestore 
                                      fontSize="small" 
                                      style={{verticalAlign: "-8%"}}
                                      />
                                    </Tooltip>
                                  </Grid>}
                                  <Grid item>
                                    {pendingMaxMonthlyBudgetString === 'No limit' &&
                                    <Typography variant="body1">{pendingMaxMonthlyBudgetString}</Typography>}
                                    {pendingMaxMonthlyBudgetString !== 'No limit' &&
                                    <Typography variant="body1">
                                      <NumericFormat
                                        value={pendingMaxMonthlyBudget}
                                        valueIsNumericString
                                        prefix={'$'} 
                                        displayType="text"
                                        decimalScale={2}
                                        fixedDecimalScale
                                        thousandSeparator/>
                                    </Typography>}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Paper className={classes.paper}>
                    <Grid container spacing={2} direction="row" >
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Grid container spacing={0}>
                              <Grid item >
                                <Typography align="left" variant="h6">Funding transactions</Typography>
                              </Grid>                          
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <TopUps auth={this.props.auth} accountId={acctId}/>
                          </Grid>

                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            );
          }
          return <div><LoadingSpinner/></div>
        }}
      />
    );
  }
  
}
  
CustomerProjectWallet.propTypes = {
    classes: PropTypes.object.isRequired,
  };
      
export default withStyles(styleSheet)(CustomerProjectWallet);
